/**
 * @file AlbuminuriaProgressListView.jsx
 * @author Fernando Ruano
 * @description Albuminuria Progress List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function AlbuminuriaProgressListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { variacion, totalItems: totalItemsParams } = useParams();

  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getAlbuminuriaProgressListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.datePenultimateAlbuminuriaRAC'),
      dataIndex: 'fecha_penultima_albuminuria',
      filter: {
        listName: 'lista_fecha_penultima_albuminuria_24h',
        searchName: 'filterFechaPenultimaAlbuminuria24h',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_penultima_albuminuria'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_penultima_albuminuria_24h',
            searchName: 'filterFechaPenultimaAlbuminuria24h'
          },
          dataIndex: 'fecha_penultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.resultsPenultimateAlbuminuriaRAC'),
      dataIndex: 'penultima_albuminuria',
      filter: {
        listName: 'lista_resultado_penultima_albuminuria_24h',
        searchName: 'filterResultadoPenultimaAlbuminuria24h',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('penultima_albuminuria'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_resultado_penultima_albuminuria_24h',
            searchName: 'filterResultadoPenultimaAlbuminuria24h'
          },
          dataIndex: 'penultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.dateLastAlbuminuriaRAC'),
      dataIndex: 'fecha_ultima_albuminuria',
      filter: {
        listName: 'lista_fecha_ultima_albuminuria_24h',
        searchName: 'filterFechaUltimaAlbuminuria24h',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_albuminuria'),
        multiple: 18
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_albuminuria_24h',
            searchName: 'filterFechaUltimaAlbuminuria24h'
          },
          dataIndex: 'fecha_ultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.resultsLastAlbuminuriaRAC'),
      dataIndex: 'ultima_albuminuria',
      filter: {
        listName: 'lista_resultado_ultima_albuminuria_24h',
        searchName: 'filterResultadoUltimaAlbuminuria24h',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultima_albuminuria'),
        multiple: 19
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_resultado_ultima_albuminuria_24h',
            searchName: 'filterResultadoUltimaAlbuminuria24h'
          },
          dataIndex: 'ultima_albuminuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.diabetesDiagnosis'),
      dataIndex: 'diagnostico_diabetes',
      filter: {
        listName: 'lista_diagnostico_diabetes',
        searchName: 'filterDiagnosticoDiabetes',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_diabetes'),
        multiple: 20
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_diabetes',
            searchName: 'filterDiagnosticoDiabetes'
          },
          dataIndex: 'diagnostico_diabetes'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.hypertensionDiagnosis'),
      dataIndex: 'diagnostico_hipertension',
      filter: {
        listName: 'lista_diagnostico_hipertension',
        searchName: 'filterDiagnosticoHipertension',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_hipertension'),
        multiple: 21
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_hipertension',
            searchName: 'filterDiagnosticoHipertension'
          },
          dataIndex: 'diagnostico_hipertension'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.glomerularDiagnosis'),
      dataIndex: 'diagnostico_enfermedad_glomerular',
      filter: {
        listName: 'lista_diagnostico_enfermedad_glomerular',
        searchName: 'filterDiagnosticoEnfermedadGlomerular',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_enfermedad_glomerular'),
        multiple: 19
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_enfermedad_glomerular',
            searchName: 'filterDiagnosticoEnfermedadGlomerular'
          },
          dataIndex: 'diagnostico_enfermedad_glomerular'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterVariacionSeleccionada: variacion,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getAlbuminuriaProgressListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_albuminuria: item?.fecha_ultima_albuminuria
              ? formatDate(item.fecha_ultima_albuminuria)
              : '',
            fecha_penultima_albuminuria: item?.fecha_penultima_albuminuria
              ? formatDate(item.fecha_penultima_albuminuria)
              : ''
          };
        });

        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
AlbuminuriaProgressListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getAlbuminuriaProgressListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default AlbuminuriaProgressListView;
